import React, { useState, useEffect } from 'react';
import '../styles/AddStreamModal.css';

const defaultFormData = {
  stream_name: '',
  stream_tab_name: '',
  description: '',
  stream_video_path: '',
  stream_video_link: '',
  stream_live_user_id: '',
  thumbnail_url: '',
  is_public: 1,
  video_file: null,
  video_youtube_link: '',
};

const AddStreamModal = ({ isOpen, onClose, onAdd, onSave, editingStream }) => {
  const [streamType, setStreamType] = useState('video');
  const [formData, setFormData] = useState(defaultFormData);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchUsers();

      if (editingStream) {
        setStreamType(editingStream.stream_type || 'video');
        setFormData({
          stream_name: editingStream.stream_name || '',
          stream_tab_name: editingStream.stream_tab_name || editingStream.stream_name || '',
          description: editingStream.description || '',
          stream_video_path: editingStream.stream_video_path || '',
          stream_video_link: editingStream.stream_video_link || '',
          stream_live_user_id: editingStream.stream_live_user_id || '',
          thumbnail_url: editingStream.thumbnail_url || '',
          is_public: editingStream.is_public === 1 ? 1 : 0,
          video_file: null,
          video_youtube_link: editingStream.video_youtube_link || '',
        });
      } else {
        resetForm(false);
      }
    }
  }, [isOpen, editingStream]);

  const fetchUsers = async () => {
    try {
      const response = await fetch('/api/users');
      const data = await response.json();
      if (response.ok) {
        setUsers(data.users || []);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        video_file: file,
        stream_video_path: '',
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.stream_name.trim()) {
      setError('Stream name is required');
      return;
    }

    if (!formData.description.trim()) {
      setError('Description is required');
      return;
    }

    if (streamType === 'video' && !formData.video_file && !formData.stream_video_path) {
      setError('Please select a video file or keep the existing one');
      return;
    }

    if (streamType === 'local_video_link' && !formData.stream_video_link.trim()) {
      setError('Please enter a local video link');
      return;
    }

    if (streamType === 'video_youtube' && !formData.video_youtube_link.trim()) {
      setError('Please enter a YouTube video ID or URL');
      return;
    }

    if (['external_link', 'video_link'].includes(streamType) && !formData.stream_video_link.trim()) {
      setError('Please enter a video link');
      return;
    }

    if (streamType === 'live' && !formData.stream_live_user_id) {
      setError('Please select a user for live streaming');
      return;
    }

    setLoading(true);
    let videoPath = formData.stream_video_path;

    try {
      if (streamType === 'video' && formData.video_file) {
        const uploadFormData = new FormData();
        uploadFormData.append('file', formData.video_file);
        uploadFormData.append('type', 'video');

        const uploadResponse = await fetch('/api/upload', {
          method: 'POST',
          body: uploadFormData,
        });

        if (!uploadResponse.ok) {
          const uploadError = await uploadResponse.json();
          throw new Error(uploadError.message || 'Failed to upload video file');
        }

        const uploadData = await uploadResponse.json();
        videoPath = uploadData.path;
      }

      const payload = {
        stream_name: formData.stream_name,
        stream_tab_name: formData.stream_tab_name || formData.stream_name,
        stream_type: streamType,
        description: formData.description,
        stream_video_path: streamType === 'video' ? videoPath : null,
        stream_video_link: ['local_video_link', 'external_link', 'video_link'].includes(streamType)
          ? formData.stream_video_link
          : null,
        stream_live_user_id: streamType === 'live' ? formData.stream_live_user_id : null,
        thumbnail_url: formData.thumbnail_url || null,
        is_public: formData.is_public ? 1 : 0,
        is_active: 1,
        video_youtube_link: streamType === 'video_youtube' ? formData.video_youtube_link : null,
      };

      const url = editingStream ? `/api/streams/${editingStream.id}` : '/api/streams/create';
      const method = editingStream ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to save stream');
      }

      if (editingStream && onSave) {
        onSave(data.stream);
      } else if (!editingStream && onAdd) {
        onAdd(data.stream);
      }

      resetForm();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = (close = true) => {
    setStreamType('video');
    setFormData(defaultFormData);
    setError('');
    if (close) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={() => resetForm(true)}>
      <div className="modal-content add-stream-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{editingStream ? 'Edit Stream' : 'Add New Stream'}</h2>
          <button className="close-btn" onClick={() => resetForm(true)}>&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="stream-form">
          <div className="form-group">
            <label>Stream Name *</label>
            <input
              type="text"
              id="stream_name"
              name="stream_name"
              value={formData.stream_name}
              onChange={handleInputChange}
              placeholder="e.g., My Live Show"
              required
            />
          </div>

          <div className="form-group">
            <label>Display Name (Tab Label)</label>
            <input
              type="text"
              id="stream_tab_name"
              name="stream_tab_name"
              value={formData.stream_tab_name}
              onChange={handleInputChange}
              placeholder="Leave blank to use stream name"
            />
          </div>

          <div className="form-group">
            <label>Stream Type *</label>
            <select
              id="stream_type"
              value={streamType}
              onChange={(e) => {
                setStreamType(e.target.value);
                setError('');
              }}
              className="stream-type-select"
              required
            >
              <option value="video">📹 Video File</option>
              <option value="local_video_link">� Local Video Link</option>
              <option value="video_youtube">📺 Embedded Video ID (YouTube, etc.)</option>
              <option value="external_link">🌐 External Links</option>
              <option value="video_link">🎬 Other Video Link</option>
              <option value="live">🔴 Live Stream</option>
            </select>
          </div>

          <div className="form-group">
            <label>Description *</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Describe your stream..."
              rows="3"
              required
            />
          </div>

          {/* Video File Input */}
          {streamType === 'video' && (
            <div className="form-group">
              <label>Video File *</label>
              <input
                type="file"
                id="stream_video_path"
                accept="video/*"
                onChange={handleFileChange}
                required={!formData.stream_video_path}
              />
              {formData.stream_video_path && (
                <p className="file-info">✅ File selected: {formData.stream_video_path.split('/').pop()}</p>
              )}
              <small>Upload a video file (MP4, WebM, etc.)</small>
            </div>
          )}

          {/* Local Video Link Input */}
          {streamType === 'local_video_link' && (
            <div className="form-group">
              <label>Local Video Link *</label>
              <input
                type="url"
                id="stream_video_link"
                name="stream_video_link"
                value={formData.stream_video_link}
                onChange={handleInputChange}
                placeholder="https://example.com/video.mp4"
                required={streamType === 'local_video_link'}
              />
              <small>Paste the direct link to your video file</small>
            </div>
          )}

          {/* Embedded YouTube Video ID Input */}
          {streamType === 'video_youtube' && (
            <div className="form-group">
              <label>YouTube Link(s) / ID(s) *</label>
              <input
                type="text"
                id="video_youtube_link"
                name="video_youtube_link"
                value={formData.video_youtube_link}
                onChange={handleInputChange}
                placeholder="e.g. PL12345, dQw4w9WgXcQ"
                required={streamType === 'video_youtube'}
              />
              <small>Enter YouTube video IDs, playlist URLs, or a comma-separated mix of both!</small>
            </div>
          )}

          {/* External Link Input */}
          {streamType === 'external_link' && (
            <div className="form-group">
              <label>External Link *</label>
              <input
                type="url"
                id="stream_video_link"
                name="stream_video_link"
                value={formData.stream_video_link}
                onChange={handleInputChange}
                placeholder="https://example.com/video"
                required={streamType === 'external_link'}
              />
              <small>Paste any external video link</small>
            </div>
          )}

          {/* Video Link Input */}
          {streamType === 'video_link' && (
            <div className="form-group">
              <label>Video Link *</label>
              <input
                type="url"
                id="stream_video_link"
                name="stream_video_link"
                value={formData.stream_video_link}
                onChange={handleInputChange}
                placeholder="https://www.youtube.com/embed/..."
                required={streamType === 'video_link'}
              />
              <small>Embed link from YouTube, Vimeo, or other video platforms</small>
            </div>
          )}

          {/* Live Stream - User Selection */}
          {streamType === 'live' && (
            <div className="form-group">
              <label>Streamer User *</label>
              <select
                id="stream_live_user_id"
                name="stream_live_user_id"
                value={formData.stream_live_user_id}
                onChange={handleInputChange}
                required={streamType === 'live'}
              >
                <option value="">-- Select a user --</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.first_name} {user.last_name} ({user.email})
                  </option>
                ))}
              </select>
              <small>Select the user who will be streaming live</small>
            </div>
          )}

          <div className="form-group">
            <label>Thumbnail URL (Optional)</label>
            <input
              type="url"
              id="thumbnail_url"
              name="thumbnail_url"
              value={formData.thumbnail_url}
              onChange={handleInputChange}
              placeholder="https://example.com/thumbnail.jpg"
            />
            <small>Upload URL for stream thumbnail/cover image</small>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                id="is_public"
                name="is_public"
                checked={formData.is_public === 1}
                onChange={(e) => setFormData(prev => ({ ...prev, is_public: e.target.checked ? 1 : 0 }))}
              />
              <span>Make this stream public</span>
            </label>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={resetForm}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Creating...' : 'Save Stream'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddStreamModal;
